/**
 * CMMC reads — V102-8-1 / V102-8-2. The scorecard, the /programs card and the
 * Today rows share these queries (react-query dedupes them), so every surface
 * renders the SAME `buildScorecard` model.
 *
 * Reads (any member; the server resolves program and period inside the
 * session workspace; a planned program is 404 without the staging preview):
 * score, affirmation gate, affirmations, POA&M, customer deadlines (H3-13); the scorecard adds requirement
 * titles (objective readiness).
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { programHas, type ProgramCard } from '@aigovops/core';
import { getAffirmationGate, getObjectiveReadiness, getProgramScore, listAffirmations, listCustomerRequirements, listProgramPoam } from './api';
import { queryKeys } from './query-keys';
import { buildScorecard, cmmcPeriod, type ScorecardModel } from './cmmc-model';

/** Programs scored by the CMMC engine (manifest-driven, never keyed on the program name). */
export const isScoredProgram = (program: string) => programHas(program, (m) => m.scoring.engine === 'cmmc');

const todayDay = () => new Date().toISOString().slice(0, 10);

export function useCmmcData(slug: string, program: string, periodId: string | null, opts: { titles?: boolean } = {}) {
  const on = !!periodId;
  const pid = periodId ?? '';
  const score = useQuery({ queryKey: queryKeys.programScore(slug, program, pid), queryFn: () => getProgramScore(slug, program, pid), enabled: on, retry: false, staleTime: 30_000 });
  const gate = useQuery({ queryKey: queryKeys.affirmationGate(slug, program, pid), queryFn: () => getAffirmationGate(slug, program, pid), enabled: on, retry: false, staleTime: 30_000 });
  const affirmations = useQuery({ queryKey: queryKeys.affirmations(slug, program), queryFn: () => listAffirmations(slug, program), enabled: on, retry: false, staleTime: 60_000 });
  const poam = useQuery({ queryKey: queryKeys.programPoam(slug, program, pid), queryFn: () => listProgramPoam(slug, program, pid), enabled: on, retry: false, staleTime: 60_000 });
  const customers = useQuery({ queryKey: queryKeys.customerRequirements(slug, program, pid), queryFn: () => listCustomerRequirements(slug, program, pid), enabled: on, retry: false, staleTime: 60_000 });
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, program, pid), queryFn: () => getObjectiveReadiness(slug, program, pid), enabled: on && !!opts.titles, retry: false, staleTime: 60_000 });
  return { score, gate, affirmations, poam, customers, readiness };
}

export interface CmmcCardState {
  card: ProgramCard;
  period: { period_id: string; label: string } | null;
  model: ScorecardModel | null;
  loading: boolean;
  error: boolean;
  prepareHref: string;
}

/** One model per scored program card (Today, /programs). */
export function useCmmcCards(slug: string, prefix: string, cards: readonly ProgramCard[]): CmmcCardState[] {
  const scored = cards.filter((c) => isScoredProgram(c.program)).map((card) => ({ card, period: cmmcPeriod(card) }));
  const results = useQueries({
    queries: scored.flatMap(({ card, period }) => {
      const pid = period?.period_id ?? '';
      const on = !!period;
      return [
        { queryKey: queryKeys.programScore(slug, card.program, pid), queryFn: () => getProgramScore(slug, card.program, pid), enabled: on, retry: false, staleTime: 30_000 },
        { queryKey: queryKeys.affirmationGate(slug, card.program, pid), queryFn: () => getAffirmationGate(slug, card.program, pid), enabled: on, retry: false, staleTime: 30_000 },
        { queryKey: queryKeys.affirmations(slug, card.program), queryFn: () => listAffirmations(slug, card.program), enabled: on, retry: false, staleTime: 60_000 },
        { queryKey: queryKeys.programPoam(slug, card.program, pid), queryFn: () => listProgramPoam(slug, card.program, pid), enabled: on, retry: false, staleTime: 60_000 },
        { queryKey: queryKeys.customerRequirements(slug, card.program, pid), queryFn: () => listCustomerRequirements(slug, card.program, pid), enabled: on, retry: false, staleTime: 60_000 },
      ];
    }),
  });
  return scored.map(({ card, period }, i) => {
    const [score, gate, aff, poam, cust] = results.slice(i * 5, i * 5 + 5) as Array<{ data?: unknown; isLoading: boolean; error: unknown }>;
    const base = `${prefix}/programs/${card.program}`;
    const s = (score?.data as { score?: Parameters<typeof buildScorecard>[0]['score'] } | undefined)?.score;
    const model = period && s ? buildScorecard({
      score: s,
      gate: (gate?.data as { gate?: Parameters<typeof buildScorecard>[0]['gate'] } | undefined)?.gate ?? null,
      affirmations: (aff?.data as { affirmations?: Parameters<typeof buildScorecard>[0]['affirmations'] } | undefined)?.affirmations,
      poam: (poam?.data as { poam?: Parameters<typeof buildScorecard>[0]['poam'] } | undefined)?.poam,
      deadlines: (cust?.data as { deadlines?: Parameters<typeof buildScorecard>[0]['deadlines'] } | undefined)?.deadlines,
      todayDay: todayDay(), prefix, program: card.program, periodId: period.period_id, tabHref: (t) => `${base}/${t}`,
    }) : null;
    return { card, period, model, loading: !!period && !!score?.isLoading, error: !!score?.error, prepareHref: `${base}/prepare` };
  });
}
